import axios from 'axios';
import { local, STORAGE_KEYS } from '../lib/storage';
import { ApiError } from './errors';

const withSlash = (url) => (url.endsWith('/') ? url : `${url}/`);

/**
 * API origin. Override with VITE_API_URL; otherwise local Nest server in
 * development and the production API everywhere else (as before).
 */
export const API_BASE_URL = withSlash(
  import.meta.env.VITE_API_URL ||
    (import.meta.env.MODE === 'development'
      ? 'https://squid-app-322vm.ondigitalocean.app/'
      : 'https://squid-app-322vm.ondigitalocean.app/')
);

/** Blog posts have always been read from production so they show up in every environment. */
export const BLOG_API_BASE_URL = withSlash(
  import.meta.env.VITE_BLOG_API_URL ||
    'https://squid-app-322vm.ondigitalocean.app/'
);

export const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60_000,
});

export const getStoredToken = () => local.get(STORAGE_KEYS.user)?.token || null;

http.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let unauthorizedHandler = null;

/** The session provider registers here to sign the user out when the token expires. */
export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
}

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && getStoredToken()) {
      unauthorizedHandler?.(error);
    }
    return Promise.reject(error);
  }
);

/** Returns `results` from the standard { results, message, error } envelope. */
export const results = (response) => response?.data?.results;

/**
 * Like `results`, but treats a 2xx response that carries a `message` and no
 * `results` as a failure (some booking endpoints report errors that way).
 */
export function requireResults(response) {
  const data = response?.data;
  if (data && data.results == null && data.message) {
    const message = Array.isArray(data.message)
      ? data.message[0]
      : data.message;
    throw new ApiError(message || 'The request could not be completed.', {
      status: response.status,
      data,
    });
  }
  return data?.results ?? null;
}
